import { Vector3 } from "three";
import type { Engine } from "../../../engine/Engine";
import { History } from "../../../history/History";
import type {
  PointerGestureMoveDetail,
  PointerGesturePressDetail,
  PointerGestureReleaseDetail,
} from "../../../input/PointerGestureInterpreter";
import type { SnapKind } from "../../../snap/SnapEngine";
import type IRoomDrawer from "../types/IRoomDrawer";
import {
  segmentPickFromDesignerTarget,
  type SegmentInteractionTarget,
} from "../segment/interaction/DesignerTarget";
import type { InteractionSession } from "../segment/interaction/session/InteractionSession";
import { pointerXzOnFloor } from "../segment/tools/planePointer";
import {
  resolveOpeningDragPointer,
  type OpeningDragPointer,
} from "../segment/tools/openingDragPointer";
import { isPerspectiveViewCamera } from "../../../engine/camera/CameraUtils";
import type { SugarModel } from "../../../loader/SugarModel";
import {
  resolveProductWallSnap,
  type MeasurementSnap,
} from "../measure/MeasurementSnapResolver";
import type {
  DesignerMoveSession,
  DesignerObject,
  DesignerPointerFeedback,
} from "./DesignerObject";
import { dispatchSegmentLengthDimension } from "../segment/dimension/dispatchSegmentLengthDimension";
import { pickVerticalGrabSurface } from "../../../util/SurfaceMovement";
import { pathPointAt } from "../../../shape/segment/axis/pathHelpers";
import type { XzPoint } from "../../../shape/segment/types";

export class SegmentDesignerObject implements DesignerObject {
  readonly id: string;

  constructor(
    readonly target: SegmentInteractionTarget,
    private readonly _drawer: IRoomDrawer,
    private readonly _engine: Engine,
    private readonly _raw: { x: number; z: number },
    private readonly _snapped: { x: number; z: number },
    private readonly _snappedKinds: SnapKind[],
    private readonly _delete: (segmentId: string) => boolean,
    private readonly _onFeedback: (feedback: DesignerPointerFeedback) => void,
  ) {
    this.id = segmentObjectId(target);
  }

  select(detail?: PointerGesturePressDetail): void {
    this._drawer.selectInteraction.selection.select(this.target);
    const segmentId = segmentIdOf(this.target);
    if (!detail || !segmentId) return;
    const entry = this._drawer.planarGraphController.planarGraph.segments.find(
      (candidate) => candidate.spec.id === segmentId,
    );
    if (!entry?.spec.placement) return;
    this._engine.dispatchCustomEvent("select-opening", {
      detail: {
        segmentId,
        event: detail,
        model: null,
      },
    });
  }

  unselect(): void {
    this._drawer.selectInteraction.selection.clear();
  }

  setHovered(active: boolean): void {
    if (active) this._drawer.selectInteraction.hover.set(this.target);
    else this._drawer.selectInteraction.hover.clear();
  }

  beginMove(detail: PointerGesturePressDetail): DesignerMoveSession | null {
    const sample = this.pointerSample(detail);
    const behavior = this._drawer.selectInteraction.behaviors.resolve(this.target);
    const session =
      behavior?.beginInteraction(this.target, sample.point, this._engine) ?? null;
    return session
      ? new SegmentMoveSession(
          session,
          this._engine,
          this,
          this._onFeedback,
          this._drawer,
        )
      : null;
  }

  pointerSample(
    detail: PointerGesturePressDetail | PointerGestureMoveDetail | PointerGestureReleaseDetail,
    liveHostId?: string,
  ): OpeningDragPointer {
    const openingId = segmentIdOf(this.target);
    const event = detail as unknown as PointerEvent;
    if (isPerspectiveViewCamera(this._engine.activeCamera?.instance) && openingId) {
      return resolveOpeningDragPointer(
        this._drawer.planarGraphController,
        this._engine,
        openingId,
        event,
        liveHostId,
      );
    }
    return { point: pointerXzOnFloor(event, this._engine) };
  }

  activate(): void {
    this._drawer.selectInteraction.behaviors
      .resolve(this.target)
      ?.activate(this.target);
  }

  delete(): boolean {
    return this.target.part === "interior"
      ? false
      : this._delete(this.target.segmentId);
  }

  pointerFeedback(): DesignerPointerFeedback {
    return {
      raw: this._raw,
      snapped: this._snapped,
      snappedKinds: this._snappedKinds,
      pickTarget: segmentPickFromDesignerTarget(this.target),
    };
  }

  measurementSnap(source: SugarModel, pointer: { x: number; z: number }): MeasurementSnap | null {
    if (this.target.part === "interior") return null;
    const segmentId = this.target.segmentId;
    const entry = this._drawer.planarGraphController.planarGraph.segments.find(
      (candidate) => candidate.spec.id === segmentId,
    );
    return entry && !entry.spec.placement
      ? resolveProductWallSnap(source, entry, pointer)
      : null;
  }
}

class SegmentMoveSession implements DesignerMoveSession {
  private _liveHostId?: string;

  constructor(
    private readonly _session: InteractionSession,
    private readonly _engine: Engine,
    private readonly _object: SegmentDesignerObject,
    private readonly _onFeedback: (feedback: DesignerPointerFeedback) => void,
    private readonly _drawer: IRoomDrawer,
  ) {
    const openingId = segmentIdOf(this._object.target);
    this._liveHostId = openingId
      ? this._drawer.planarGraphController.planarGraph.segments.find(
          (candidate) => candidate.spec.id === openingId,
        )?.spec.placement?.hostSegmentId
      : undefined;
  }

  begin(_input?: void): void {}

  move(detail: PointerGestureMoveDetail): void {
    this.update(detail);
  }

  /** Yatay/Dikey her frame okunur — ctor’da dondurulmaz. */
  private _isOpeningVerticalMove(): boolean {
    if (this._drawer.axisMoveMode !== "vertical") return false;
    if (this._object.target.part !== "body") return false;
    if (!this._session.updateElevation) return false;
    const segmentId = segmentIdOf(this._object.target);
    if (!segmentId) return false;
    const entry = this._drawer.planarGraphController.planarGraph.segments.find(
      (candidate) => candidate.spec.id === segmentId,
    );
    return Boolean(entry?.spec.placement);
  }

  private _openingAnchor(): Vector3 {
    const segmentId = segmentIdOf(this._object.target);
    const entry = segmentId
      ? this._drawer.planarGraphController.planarGraph.segments.find(
          (candidate) => candidate.spec.id === segmentId,
        )
      : undefined;
    const center = entry
      ? pathPointAt(entry.spec.path, 0.5)
      : { x: 0, z: 0 };
    const bottom = entry?.spec.placement?.bottom ?? 0;
    const height = entry?.spec.placement?.height ?? 2.1;
    return new Vector3(center.x, bottom + height * 0.5, center.z);
  }

  update(detail: PointerGestureMoveDetail): void {
    if (this._isOpeningVerticalMove()) {
      const y = this._verticalWorldY(detail);
      if (y === undefined || !this._session.updateElevation) return;
      this._emit(this._session.updateElevation(y));
      return;
    }
    const sample = this._object.pointerSample(detail, this._liveHostId);
    this._emit(this._session.update(sample.point, { hitHostId: sample.hitHostId }), sample.point);
  }

  commit(detail: PointerGestureReleaseDetail): void {
    if (this._isOpeningVerticalMove() && this._session.updateElevation) {
      const y = this._verticalWorldY(detail);
      if (y !== undefined) this._session.updateElevation(y);
    } else {
      const sample = this._object.pointerSample(detail, this._liveHostId);
      this._session.update(sample.point, { hitHostId: sample.hitHostId });
    }
    const result = this._session.commit();
    dispatchSegmentLengthDimension(this._engine, null);
    if (result.changed) {
      History.of(this._engine)?.snapshot(result.historyLabel ?? "Değişiklik");
    }
  }

  cancel(): void {
    this._session.cancel();
    dispatchSegmentLengthDimension(this._engine, null);
  }

  dispose(): void {
    this._session.dispose();
  }

  private _verticalWorldY(
    detail: PointerGestureMoveDetail | PointerGestureReleaseDetail,
  ): number | undefined {
    const camera = this._engine.activeCamera?.instance;
    const dom = this._engine.canvas;
    if (!camera || !dom) return undefined;
    return pickVerticalGrabSurface(this._openingAnchor(), {
      event: detail as unknown as PointerEvent,
      camera,
      dom,
    }).point.y;
  }

  private _emit(
    update: ReturnType<InteractionSession["update"]>,
    raw?: XzPoint,
  ): void {
    if (update.draftOpening) {
      this._liveHostId = update.draftOpening.host.id;
      dispatchSegmentLengthDimension(this._engine, update.draftOpening.opening, {
        hostSpec: update.draftOpening.host,
        editable: false,
      });
    }
    this._onFeedback({
      raw: raw ?? update.snapped,
      snapped: update.snapped,
      snappedKinds: update.snappedKinds,
      pickTarget: this._object.pointerFeedback().pickTarget,
    });
  }
}

function segmentIdOf(target: SegmentInteractionTarget): string | undefined {
  return target.part === "interior" ? undefined : target.segmentId;
}

function segmentObjectId(target: SegmentInteractionTarget): string {
  if (target.part === "interior") return `segment-interior:${target.faceIndex}`;
  if (target.part === "endpoint") {
    return `segment:${target.segmentId}:endpoint:${target.role}`;
  }
  if (target.part === "thickness") {
    return `segment:${target.segmentId}:thickness:${target.side}`;
  }
  return `segment:${target.segmentId}`;
}
